import { Routes, Route, Navigate } from 'react-router-dom'
import './i18n/config'
import { getCountryFromSubdomain, SUBDOMAIN_TO_COUNTRY } from './config/countryThemes'
import { COUNTRIES } from './config/countries'
import CountrySelector from './components/CountrySelector'
import CountryHome from './components/home/CountryHome'
import SubdomainLayout from './components/layout/SubdomainLayout'
import Login from './components/auth/Login'
import Register from './components/auth/Register'
import ForgotPassword from './components/auth/ForgotPassword'
import ResetPassword from './components/auth/ResetPassword'
import VerifyEmail from './components/auth/VerifyEmail'
import Dashboard from './components/dashboard/Dashboard'
import ContractWizard from './components/contracts/ContractWizard'
import ContractPreview from './components/contracts/ContractPreview'
import ContractDownload from './components/contracts/ContractDownload'
import { TermsOfService, PrivacyPolicy, About, Contact, NotFound } from './components/pages'
import { AdminLayout, AdminDashboard, AdminUsers, AdminContracts, AdminSettings, AdminCountries, AdminTemplates } from './components/admin'

// PrivateRoute: requires authenticated user in localStorage
const PrivateRoute = ({ children }) => {
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  return children;
};

// Detect country from subdomain (e.g., us.rentydoc.com -> 'usa')
const subdomainCountry = getCountryFromSubdomain(window.location.hostname);
const isOnSubdomain = !!subdomainCountry;

// Legacy route redirect: /:country/:lang/* -> subdomain clean URL
const LegacyRedirect = () => {
  const country = window.location.pathname.split('/')[1];
  const countryData = COUNTRIES[country];
  if (countryData) {
    // Strip /:country/:lang from path, keep the rest
    const pathParts = window.location.pathname.split('/');
    const remainingPath = pathParts.slice(3).join('/'); // skip '', country, lang
    const cleanPath = remainingPath ? `/${remainingPath}` : '/';
    const targetUrl = `${window.location.protocol}//${countryData.subdomain}.rentydoc.com${cleanPath}`;
    window.location.href = targetUrl;
    return null;
  }
  return <NotFound />;
};

function App() {
  if (isOnSubdomain) {
    // SUBDOMAIN ROUTES: clean URLs, no country/lang in path
    // SubdomainLayout provides themed header, footer, and sets i18n language
    return (
      <Routes>
        {/* Routes WITH themed layout (header + footer) */}
        <Route element={<SubdomainLayout />}>
          {/* Country home */}
          <Route path="/" element={<CountryHome />} />

          {/* Contract routes */}
          <Route path="/contracts/create" element={<ContractWizard />} />
          <Route path="/contracts/:contractId/preview" element={<ContractPreview />} />
          <Route path="/contracts/:contractId/download" element={<ContractDownload />} />

          {/* Dashboard */}
          <Route path="/dashboard" element={<Dashboard />} />

          {/* Legal and static pages */}
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />

          {/* 404 catch-all inside layout */}
          <Route path="*" element={<NotFound />} />
        </Route>

        {/* Auth routes - own layout (split screen design) */}
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />

        {/* Admin routes - own layout, requires authentication */}
        <Route path="/admin" element={<PrivateRoute><AdminLayout /></PrivateRoute>}>
          <Route index element={<AdminDashboard />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="contracts" element={<AdminContracts />} />
          <Route path="settings" element={<AdminSettings />} />
          <Route path="analytics" element={<AdminDashboard />} />
          <Route path="payments" element={<AdminContracts />} />
          <Route path="countries" element={<AdminCountries />} />
          <Route path="templates" element={<AdminTemplates />} />
          <Route path="logs" element={<AdminUsers />} />
        </Route>

        {/* Legacy /:country/:lang/* routes on subdomain -> redirect to clean URL */}
        <Route path="/:country/:lang/*" element={<LegacySubdomainRedirect />} />
        <Route path="/:country/:lang" element={<LegacySubdomainRedirect />} />
      </Routes>
    )
  }

  // MAIN DOMAIN ROUTES: rentydoc.com
  return (
    <Routes>
      {/* Root: country selector */}
      <Route path="/" element={<CountrySelector />} />

      {/* Legacy /:country/:lang/* routes -> redirect to subdomain */}
      <Route path="/:country/:lang/*" element={<LegacyRedirect />} />
      <Route path="/:country/:lang" element={<LegacyRedirect />} />

      {/* Auth routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/dashboard" element={<Dashboard />} />

      {/* Legal and static pages */}
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/about" element={<About />} />
      <Route path="/contact" element={<Contact />} />

      {/* Admin routes - requires authentication */}
      <Route path="/admin" element={<PrivateRoute><AdminLayout /></PrivateRoute>}>
        <Route index element={<AdminDashboard />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="contracts" element={<AdminContracts />} />
        <Route path="settings" element={<AdminSettings />} />
        <Route path="analytics" element={<AdminDashboard />} />
        <Route path="payments" element={<AdminContracts />} />
        <Route path="countries" element={<AdminCountries />} />
        <Route path="templates" element={<AdminTemplates />} />
        <Route path="logs" element={<AdminUsers />} />
      </Route>

      {/* 404 catch-all */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  )
}

// On subdomain, redirect legacy /:country/:lang/... to clean /...
const LegacySubdomainRedirect = () => {
  const pathParts = window.location.pathname.split('/');
  const remainingPath = pathParts.slice(3).join('/');
  const cleanPath = remainingPath ? `/${remainingPath}` : '/';
  return <Navigate to={cleanPath} replace />;
};

export default App
